import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { TycoonState } from "../api/tycoon.api";

/**
 * 2D top-down cutaway of a currency-exchange office building. Two stacked
 * rooms in one SVG:
 *
 *   - Back office (top, Y 0..280): support staff workspace.
 *       Security station (CCTV monitor wall + desks) on the left,
 *       Marketing bullpen (whiteboard + dual-monitor desks) in the middle,
 *       Manager's executive office (big desk, chairs, awards) on the right.
 *
 *   - Front office (bottom, Y 300..750): customer-facing branch.
 *       Tellers behind glass counter at top, customer area with bench /
 *       ATM / plants below, animated customers walking from the entrance
 *       to a window and back out. Identical to the original layout, just
 *       shifted down by FRONT_OFFICE_Y.
 *
 * A short hallway separates the two rooms (Y 280..300).
 *
 * Visual-only: simulation is independent of server-side income.
 */

type StaffMember = TycoonState["staff"][number];
type Branch = TycoonState["branches"][number];

// Role types that are hireable directly from a back-office seat tap.
// Originally just SupportRole (MANAGER / MARKETING / SECURITY); widened
// when the Trade Hub absorbed TRADER + QUANT seats into the back office.
export type SupportRole = "MANAGER" | "MARKETING" | "SECURITY";
export type BackOfficeRole = SupportRole | "TRADER" | "QUANT";

interface Props {
  branch: Branch;
  staff: StaffMember[];
  /** Tap-to-hire handler. Fires when an empty desk is clicked. */
  onHire?: (branchId: string, role: BackOfficeRole) => void;
  /** Tap-to-upgrade handler. Fires when an occupied desk is clicked. */
  onUpgrade?: (staffId: string, currentLevel: number) => void;
  /** Disable interactions while a request is in flight. */
  busy?: boolean;
}

// SVG viewBox dimensions — back office (280) + hallway (20) + front office (450).
const VIEW_W = 800;
const VIEW_H = 750;

// ─── Front office (Y range 0..450 in its local coord system) ────────
// All front office children are rendered inside a translate(0, FRONT_OFFICE_Y)
// group so these constants stay relative to the room and aren't affected
// by the back office added on top.

const FRONT_OFFICE_Y = 300;
const BACK_WALL_H = 70;
const COUNTER_Y = 218;
const COUNTER_H = 22;
const GLASS_Y_TOP = 178;
const GLASS_H = 40;
const FRONT_OFFICE_HEIGHT = 450;

// Counter window centers (X). Up to 3 visible service positions.
const WINDOW_X = [200, 400, 600];
const STAFF_CHAIR_Y = 130;
const STAFF_DESK_Y = 150;
const STAFF_HEAD_Y = 195;

// Customer area Y bands (front-office-local).
const SERVE_Y = 252;
const WAITING_BENCH_Y = 360;
const ENTRANCE_X = 400;
const ENTRANCE_Y = 430;

// ─── Back office (Y range 0..280 in viewBox) ─────────────────────────

const BACK_OFFICE_HEIGHT = 280;
const BACK_OFFICE_TITLE_H = 24;

// Three side-by-side zones inside the back office.
//   - Security station (left): up to 2 SECURITY guards
//   - Trade Hub (middle): 2 TRADER desks + 1 QUANT desk
//   - Manager Office (right): MANAGER + co-located MARKETING manager
const SECURITY_ZONE = { x: 0, w: 260 };
const TRADE_HUB_ZONE = { x: 260, w: 260 };
const MANAGER_ZONE = { x: 520, w: 280 };

// Hallway between back and front office.
const HALLWAY_Y = BACK_OFFICE_HEIGHT;
const HALLWAY_H = FRONT_OFFICE_Y - BACK_OFFICE_HEIGHT;

const STAFF_COLOR: Record<string, { suit: string; head: string }> = {
  CASHIER:   { suit: "#1f6feb", head: "#e8c39e" },
  TRADER:    { suit: "#1f8a4f", head: "#d9a880" },
  QUANT:     { suit: "#7c3aed", head: "#e8c39e" },
  MANAGER:   { suit: "#d97706", head: "#e8c39e" },
  MARKETING: { suit: "#ec4899", head: "#d9a880" },
  SECURITY:  { suit: "#1f2937", head: "#c89878" }, // dark uniform
};

const STAFF_LABEL: Record<string, string> = {
  CASHIER: "Cashier",
  TRADER: "Trader",
  QUANT: "Quant",
  MANAGER: "Manager",
  MARKETING: "Marketing",
  SECURITY: "Security",
};

// Counter windows in the front office hold cashiers only — traders and
// quants moved to the back-office Trade Hub.
const FRONT_OFFICE_TYPES = ["CASHIER"] as const;

/**
 * Polished role character. Drawn centered on (0, 0):
 *   - eyes around y = -3 (head center)
 *   - shoulders around y = +5
 *   - feet/floor shadow around y = +12
 *
 * Caller wraps with <g transform="translate(x, y)" /> where y is roughly
 * where the figure's anchor (between head and shoulders) should sit.
 *
 * Each role gets a distinguishing prop drawn directly on the figure:
 *   CASHIER   — green visor + small chest tie
 *   TRADER    — short messy hair, no tie (casual desk role)
 *   QUANT     — round glasses + curly hair
 *   MANAGER   — side-parted hair + red tie
 *   MARKETING — sleek bob + yellow lanyard with badge
 *   SECURITY  — black peaked cap + hi-vis chest stripe
 *
 * Eyes are 2 small dots so figures don't read as faceless. Idle motion
 * (bob/sway/scan) is built-in; pass `withIdle={false}` to disable.
 */
function RoleCharacter({
  type,
  level,
  compact = false,
  withIdle = true,
}: {
  type: string;
  level: number;
  compact?: boolean;
  withIdle?: boolean;
}) {
  const color = STAFF_COLOR[type] ?? STAFF_COLOR.CASHIER;
  const glow = 1 + level * 0.5;

  // Bumped ~30% over the original sizing — at the actual on-screen scale
  // (~5px tall figures on a 350px viewport), sub-pixel offsets between body
  // parts read as overlap. Larger figures let the role props (visor, hat,
  // glasses) actually be visible.
  const shoulderRy = compact ? 9 : 11;
  const shoulderRx = compact ? 14 : 17;
  // Face is an OVAL (rx slightly less than ry) — a perfect circle reads
  // as a generic blob; an oval reads as a face. Cheekbones implied at
  // the wider middle, chin at the narrower bottom curve.
  const headRx = compact ? 5.6 : 6.5;
  const headRy = compact ? 7.0 : 8.2;
  const headY = -4;

  // Per-role idle motion. Tiny amplitudes — meant to read as ambient life,
  // not slapstick. Each pattern matches the role's vibe (security scans,
  // marketing types, manager nods).
  const idle =
    type === "MANAGER" ? { y: [0, -0.6, 0.3, 0] }
    : type === "MARKETING" ? { x: [0, -0.7, 0.7, -0.4, 0] }
    : type === "SECURITY" ? { rotate: [-3, 4, -3] }
    : type === "TRADER" ? { y: [0, -0.4, 0.2, 0] }
    : type === "QUANT" ? { y: [0, -0.3, 0.15, 0] }
    : { y: [0, -0.5, 0.25, 0] }; // CASHIER + fallback
  const dur = type === "SECURITY" ? 4.5 : type === "MARKETING" ? 1.2 : 3.6;

  // Per-role floor disc emoji indicator. The disc itself uses the role's
  // suit color so each character has an unmistakable identity at thumbnail
  // size — readable from anywhere in the office cutaway.
  const roleEmoji =
    type === "CASHIER" ? "💵"
    : type === "TRADER" ? "📈"
    : type === "QUANT" ? "🧮"
    : type === "MANAGER" ? "👔"
    : type === "MARKETING" ? "📣"
    : type === "SECURITY" ? "🛡"
    : "•";

  return (
    <g style={{ filter: `drop-shadow(0 0 ${glow}px ${color.suit})` }}>
      {/* Colored floor disc — role identity at a glance, even at thumbnail
       * size. Outside the idle group so it stays anchored. */}
      <ellipse
        cx={0}
        cy={shoulderRy + 8}
        rx={shoulderRx + 1}
        ry="2.4"
        fill={color.suit}
        opacity="0.45"
      />
      <ellipse
        cx={0}
        cy={shoulderRy + 8}
        rx={shoulderRx - 1}
        ry="1.8"
        fill="rgba(0,0,0,0.5)"
      />

      <motion.g
        animate={withIdle ? idle : undefined}
        transition={withIdle ? { duration: dur, repeat: Infinity, ease: "easeInOut" } : undefined}
      >
        {/* Suit / shoulders */}
        <ellipse cx={0} cy={shoulderRy - 1} rx={shoulderRx} ry={shoulderRy} fill={color.suit} />
        {/* Highlight pass — adds dimensionality without needing a defined gradient */}
        <ellipse
          cx={-shoulderRx * 0.35}
          cy={shoulderRy - 3}
          rx={shoulderRx * 0.55}
          ry={shoulderRy * 0.55}
          fill="rgba(255,255,255,0.07)"
        />

        {/* Neck — short skin column from chin down into the collar */}
        <rect
          x={-1.8}
          y={headY + headRy - 1}
          width="3.6"
          height="2.2"
          fill={color.head}
        />

        {/* Collar — small horizontal white bar that sits below the chin at
         * the suit's neckline. */}
        <rect
          x={-3.5}
          y={headY + headRy + 1}
          width="7"
          height="2"
          rx="0.5"
          fill="#eaecef"
        />

        {/* STELLEX wordmark on the chest. Always rendered in white for
         * contrast against the colored shirt. Positioned below the collar
         * and above any role accessory (tie / lanyard / hi-vis stripe). */}
        <text
          x={0}
          y={shoulderRy - 1.5}
          textAnchor="middle"
          fontSize={compact ? 2.5 : 3}
          fontWeight="900"
          fill="#ffffff"
          letterSpacing="0.15"
          style={{ pointerEvents: "none", fontFamily: "Inter, system-ui, sans-serif" }}
        >
          STELLEX
        </text>

        {/* Tie — MANAGER only. Anchors below the collar, sits ON TOP of
         * the STELLEX wordmark area (text is rendered first in this
         * group's order, so the tie overlays it cleanly). */}
        {type === "MANAGER" && (
          <path
            d={`M -1.6 ${headY + headRy + 3} L -1 ${shoulderRy + 2} L 0 ${shoulderRy + 5} L 1 ${shoulderRy + 2} L 1.6 ${headY + headRy + 3} Z`}
            fill="#d63838"
            stroke="rgba(0,0,0,0.4)"
            strokeWidth="0.3"
          />
        )}

        {/* Lanyard + badge for marketing — drawn AFTER the wordmark so the
         * yellow strip + ID badge cover the centered "STELLEX" text. */}
        {type === "MARKETING" && (
          <g>
            <rect
              x={-1.5}
              y={headY + headRy + 3}
              width="3"
              height={shoulderRy - 2}
              fill="#f0b90b"
              rx="0.4"
              stroke="rgba(0,0,0,0.4)"
              strokeWidth="0.3"
            />
            <rect
              x={-4}
              y={shoulderRy + 1}
              width="8"
              height="4.5"
              fill="#f0b90b"
              rx="0.7"
              stroke="#bf8a08"
              strokeWidth="0.4"
            />
          </g>
        )}

        {/* Hi-vis vest stripes for security */}
        {type === "SECURITY" && (
          <g stroke="#f0b90b" strokeWidth="2" fill="none" opacity="0.9">
            <path d={`M ${-shoulderRx + 3} ${shoulderRy - 4} Q 0 ${shoulderRy - 6} ${shoulderRx - 3} ${shoulderRy - 4}`} />
            <path d={`M ${-shoulderRx + 3} ${shoulderRy + 1} Q 0 ${shoulderRy - 1} ${shoulderRx - 3} ${shoulderRy + 1}`} strokeWidth="1.5" />
          </g>
        )}

        {/* Head — OVAL not circle. rx narrower than ry gives the egg shape
         * that reads as a face (chin tapers, cheekbones at the wide middle). */}
        <ellipse
          cx={0}
          cy={headY}
          rx={headRx}
          ry={headRy}
          fill={color.head}
          stroke="rgba(0,0,0,0.4)"
          strokeWidth="0.6"
        />
        {/* Subtle cheek shading — narrow vertical highlight in the center
         * for a tiny bit of depth without crossing into uncanny territory. */}
        <ellipse
          cx={0}
          cy={headY + 0.5}
          rx={headRx * 0.45}
          ry={headRy * 0.55}
          fill="rgba(255,255,255,0.05)"
        />

        {/* Eyes — slightly closer together to fit the narrower oval face */}
        <circle cx={-2} cy={headY - 0.5} r="1.2" fill="#1a1208" />
        <circle cx={2} cy={headY - 0.5} r="1.2" fill="#1a1208" />
        {/* Catchlights */}
        <circle cx={-1.65} cy={headY - 0.8} r="0.4" fill="#ffffff" opacity="0.9" />
        <circle cx={2.35} cy={headY - 0.8} r="0.4" fill="#ffffff" opacity="0.9" />

        {/* Smile — slightly narrower to fit the tapered chin */}
        <path
          d={`M -1.6 ${headY + 2.2} Q 0 ${headY + 3.6} 1.6 ${headY + 2.2}`}
          stroke="#3a1a08"
          strokeWidth="0.7"
          fill="none"
          strokeLinecap="round"
        />

        {/* Quant glasses — also follow the slimmer eye spacing */}
        {type === "QUANT" && (
          <g stroke="#0a0a0a" strokeWidth="0.8" fill="none">
            <circle cx={-2} cy={headY - 0.5} r="2.2" />
            <circle cx={2} cy={headY - 0.5} r="2.2" />
            <line x1={-0.2} y1={headY - 0.5} x2={0.2} y2={headY - 0.5} strokeWidth="0.6" />
          </g>
        )}

        {/* Hair / hat — per role. X spans use headRx (head width); Y uses
         * headRy (head height). Always anchored above the eye line. */}
        {type === "SECURITY" ? (
          <g>
            {/* Cap dome */}
            <path
              d={`M ${-headRx - 0.5} ${headY - headRy + 1.5} Q 0 ${headY - headRy - 5} ${headRx + 0.5} ${headY - headRy + 1.5} Z`}
              fill="#0a0a0a"
              stroke="#1a1a1a"
              strokeWidth="0.3"
            />
            {/* Brim — wider than the head so it visibly reads as a peak */}
            <ellipse cx={0} cy={headY - headRy + 1.8} rx={headRx + 2.5} ry="1" fill="#0a0a0a" stroke="#1a1a1a" strokeWidth="0.3" />
            {/* Gold badge */}
            <circle cx={0} cy={headY - headRy - 1.5} r="1.2" fill="#f0b90b" stroke="#bf8a08" strokeWidth="0.3" />
          </g>
        ) : type === "MANAGER" ? (
          // Side-parted dark hair
          <g fill="#3a2a1a">
            <path d={`M ${-headRx + 0.3} ${headY - 0.5} Q ${-headRx + 1.5} ${headY - headRy - 2} ${headRx - 0.5} ${headY - headRy - 2} Q ${headRx + 0.2} ${headY - 0.5} ${headRx + 0.3} ${headY + 1}`} />
          </g>
        ) : type === "MARKETING" ? (
          // Sleek bob — frames the oval face
          <path
            d={`M ${-headRx - 0.3} ${headY + 1.5} Q ${-headRx + 0.5} ${headY - headRy - 2} 0 ${headY - headRy - 2} Q ${headRx - 0.5} ${headY - headRy - 2} ${headRx + 0.3} ${headY + 1.5}`}
            fill="#1a0d08"
          />
        ) : type === "QUANT" ? (
          // Curly mop — fluffy crown
          <g fill="#2a1d12">
            <circle cx={-headRx + 0.5} cy={headY - headRy + 0.5} r="1.6" />
            <circle cx={-1.5} cy={headY - headRy - 1} r="1.5" />
            <circle cx={1.5} cy={headY - headRy - 1.2} r="1.5" />
            <circle cx={headRx - 0.5} cy={headY - headRy + 0.5} r="1.6" />
          </g>
        ) : type === "TRADER" ? (
          // Short tousled cut
          <path
            d={`M ${-headRx + 0.3} ${headY - headRy + 1.5} Q ${-2.5} ${headY - headRy - 3} 0 ${headY - headRy - 2} Q ${2.5} ${headY - headRy - 3} ${headRx - 0.3} ${headY - headRy + 1.5}`}
            fill="#3a2818"
          />
        ) : (
          // CASHIER — green visor + neat hair underneath
          <g>
            {/* Hair under visor */}
            <path d={`M ${-headRx + 0.3} ${headY - headRy + 2} Q 0 ${headY - headRy - 1} ${headRx - 0.3} ${headY - headRy + 2}`} fill="#2a1d12" />
            {/* Visor dome */}
            <path
              d={`M ${-headRx - 0.5} ${headY - headRy + 2.8} Q 0 ${headY - headRy - 2} ${headRx + 0.5} ${headY - headRy + 2.8} L ${headRx + 0.5} ${headY - headRy + 1.8} Q 0 ${headY - headRy - 3} ${-headRx - 0.5} ${headY - headRy + 1.8} Z`}
              fill="#0fa86a"
              stroke="#0a3a26"
              strokeWidth="0.4"
            />
            {/* Visor band */}
            <rect x={-headRx - 0.5} y={headY - headRy + 1.5} width={(headRx + 0.5) * 2} height="0.9" fill="#0a3a26" rx="0.3" />
          </g>
        )}

        {/* Marketing typing flicker */}
        {type === "MARKETING" && (
          <motion.circle
            cx={4}
            cy={shoulderRy + 4}
            r="0.8"
            fill="#eaecef"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: dur / 4, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
      </motion.g>

      {/* Role emoji floating above head — readable at any zoom, makes the
       * role IMMEDIATELY identifiable. Outside idle group so it stays put. */}
      <text
        x={0}
        y={headY - headRy - 4}
        textAnchor="middle"
        fontSize="6"
        style={{ pointerEvents: "none" }}
      >
        {roleEmoji}
      </text>
    </g>
  );
}

interface CustomerSprite {
  id: string;
  windowIdx: number;
  variant: number;  // skin/coat color variant 0-3
}

interface CoinFloaty {
  id: string;
  x: number;
  y: number;
  amount: number;
}

const CUSTOMER_VARIANTS = [
  { coat: "#3a3a3a", head: "#e8c39e" },  // gray coat, light skin
  { coat: "#5a3a2a", head: "#a87852" },  // brown coat, medium skin
  { coat: "#1f3a4a", head: "#c89878" },  // navy coat, tan skin
  { coat: "#7a2a2a", head: "#7a4828" },  // burgundy coat, dark skin
  { coat: "#2a4a3a", head: "#e8c39e" },  // green coat, light
];

export function OfficeFloor({ branch, staff, onHire, onUpgrade, busy = false }: Props) {
  const [customers, setCustomers] = useState<CustomerSprite[]>([]);
  const [floaties, setFloaties] = useState<CoinFloaty[]>([]);
  const idCounter = useRef(0);

  // Bucket staff into the rooms they belong in. Counter windows only fit
  // CASHIER (front office); back-office Trade Hub takes traders + quants;
  // back-office Manager Office takes the manager + marketing manager.
  // Overflow beyond visible capacity is tracked in game logic but not drawn.
  const counterStaff = staff
    .filter((s) => (FRONT_OFFICE_TYPES as readonly string[]).includes(s.type))
    .slice(0, 3);
  const managers = staff.filter((s) => s.type === "MANAGER").slice(0, 1);
  const marketers = staff.filter((s) => s.type === "MARKETING").slice(0, 1);
  const traders = staff.filter((s) => s.type === "TRADER").slice(0, 2);
  const quants = staff.filter((s) => s.type === "QUANT").slice(0, 1);
  const guards = staff.filter((s) => s.type === "SECURITY").slice(0, 2);
  // Used for the customer arrival rate — only counter staff matter for
  // walk-in flow.
  const visibleStaff = counterStaff;
  const totalHourly = staff.reduce((s, x) => s + Number(x.hourlyTokenRate), 0);

  useEffect(() => {
    if (visibleStaff.length === 0) {
      setCustomers([]);
      return;
    }
    const intervalMs = Math.max(1100, 4500 / visibleStaff.length);
    const id = window.setInterval(() => {
      const windowIdx = Math.floor(Math.random() * visibleStaff.length);
      idCounter.current += 1;
      setCustomers((prev) => {
        const next: CustomerSprite[] = [
          ...prev,
          {
            id: `c${idCounter.current}`,
            windowIdx,
            variant: Math.floor(Math.random() * CUSTOMER_VARIANTS.length),
          },
        ];
        return next.length > 12 ? next.slice(-12) : next;
      });
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [visibleStaff.length]);

  const handleServed = (windowIdx: number) => {
    const member = visibleStaff[windowIdx];
    if (!member) return;
    const amount = Math.max(1, Math.round(Number(member.hourlyTokenRate) / 30));
    idCounter.current += 1;
    const floatyId = `f${idCounter.current}`;
    setFloaties((prev) => [
      ...prev,
      { id: floatyId, x: WINDOW_X[windowIdx], y: COUNTER_Y - 4, amount },
    ]);
    window.setTimeout(() => {
      setFloaties((prev) => prev.filter((f) => f.id !== floatyId));
    }, 1300);
  };

  const handleCompleted = (customerId: string) => {
    setCustomers((prev) => prev.filter((c) => c.id !== customerId));
  };

  return (
    <div className="office-floor">
      <svg
        className="office-svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <Defs />

        {/* ─── Back office (top room) ─────────────────────── */}
        <BackOffice
          branchId={branch.id}
          managers={managers}
          marketers={marketers}
          guards={guards}
          traders={traders}
          quants={quants}
          onHire={onHire}
          onUpgrade={onUpgrade}
          busy={busy}
        />

        {/* ─── Hallway divider between back and front ──────── */}
        <Hallway />

        {/* ─── Front office (bottom room) ──────────────────── */}
        <g transform={`translate(0, ${FRONT_OFFICE_Y})`}>
          <Floor />
          <FloorLights />
          <BackWall />
          <RateBoard />
          <CornerPlant x={40} y={250} />
          <CornerPlant x={760} y={250} flip />
          <Bench x={130} y={WAITING_BENCH_Y} />
          <Atm x={655} y={335} />
          <Entrance x={ENTRANCE_X} y={ENTRANCE_Y} />
          <SecurityCam x={760} y={20} />
          <SecurityCam x={40} y={20} flip />

          {/* Teller area (behind counter): chairs, desks, monitors, staff */}
          {counterStaff.map((s, idx) => (
            <TellerStation
              key={s.id}
              x={WINDOW_X[idx]}
              type={s.type}
              level={s.level}
            />
          ))}

          {/* Counter & glass on top of teller area to give "behind glass" feel */}
          <Counter />
          <Glass />
          <CounterWindows count={counterStaff.length} />

          {/* Animated agents (positions are front-office-local thanks to the
              wrapping translate group). */}
          <AnimatePresence>
            {customers.map((c) => (
              <Customer
                key={c.id}
                customerId={c.id}
                windowIdx={c.windowIdx}
                variant={c.variant}
                onServed={handleServed}
                onCompleted={handleCompleted}
              />
            ))}
          </AnimatePresence>

          <AnimatePresence>
            {floaties.map((f) => (
              <Floaty key={f.id} {...f} />
            ))}
          </AnimatePresence>

          {counterStaff.length === 0 && <ClosedSign />}
        </g>
      </svg>

      {/* HUD readout (HTML, on top of SVG) */}
      <div className="office-hud">
        <span className="office-hud-rate">{totalHourly.toLocaleString()}</span>
        <span className="office-hud-unit">/hr</span>
      </div>
    </div>
  );
}

// ─── Static scenery ──────────────────────────────────────────────────────

function Defs() {
  return (
    <defs>
      <linearGradient id="floorGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1a1f29" />
        <stop offset="1" stopColor="#0c0f15" />
      </linearGradient>
      <pattern id="floorTile" width="44" height="44" patternUnits="userSpaceOnUse">
        <rect width="44" height="44" fill="url(#floorGrad)" />
        <path
          d="M0 0 L44 0 M0 44 L44 44 M0 0 L0 44 M44 0 L44 44"
          stroke="rgba(255,255,255,0.025)"
          strokeWidth="0.5"
        />
      </pattern>
      <linearGradient id="counterGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#7a5a3e" />
        <stop offset="0.5" stopColor="#4a3724" />
        <stop offset="1" stopColor="#2e2216" />
      </linearGradient>
      <linearGradient id="counterTopGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#8a6a4a" />
        <stop offset="0.5" stopColor="#a08560" />
        <stop offset="1" stopColor="#8a6a4a" />
      </linearGradient>
      <linearGradient id="glassGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="rgba(170,210,240,0.12)" />
        <stop offset="0.4" stopColor="rgba(170,210,240,0.06)" />
        <stop offset="1" stopColor="rgba(170,210,240,0.18)" />
      </linearGradient>
      <linearGradient id="wallGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1c2230" />
        <stop offset="1" stopColor="#0f131a" />
      </linearGradient>
      <radialGradient id="lightGrad">
        <stop offset="0" stopColor="rgba(240,200,120,0.10)" />
        <stop offset="0.7" stopColor="rgba(240,200,120,0.02)" />
        <stop offset="1" stopColor="rgba(240,200,120,0)" />
      </radialGradient>
      <linearGradient id="monitorGlow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1e3a5f" />
        <stop offset="1" stopColor="#0a1424" />
      </linearGradient>
      <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="1.5" />
      </filter>

      {/* Back office assets */}
      <pattern id="backOfficeTile" width="40" height="40" patternUnits="userSpaceOnUse">
        <rect width="40" height="40" fill="#0e1117" />
        <path
          d="M0 0 L40 0 M0 40 L40 40 M0 0 L0 40 M40 0 L40 40"
          stroke="rgba(255,255,255,0.02)"
          strokeWidth="0.5"
        />
      </pattern>
      <linearGradient id="cctvFeed" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#0a141e" />
        <stop offset="0.5" stopColor="#0e1a28" />
        <stop offset="1" stopColor="#06080c" />
      </linearGradient>
      <linearGradient id="execDeskGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5a3a1e" />
        <stop offset="0.5" stopColor="#3a2410" />
        <stop offset="1" stopColor="#1f1408" />
      </linearGradient>
      <linearGradient id="execDeskTop" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#7a4f28" />
        <stop offset="0.5" stopColor="#9c6a3a" />
        <stop offset="1" stopColor="#7a4f28" />
      </linearGradient>
      <linearGradient id="leatherChair" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1a0a0a" />
        <stop offset="1" stopColor="#3a0808" />
      </linearGradient>
      <linearGradient id="whiteboardGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f4f4f4" />
        <stop offset="1" stopColor="#cfcfcf" />
      </linearGradient>
      <linearGradient id="chartLine" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#0ecb81" />
        <stop offset="1" stopColor="#f0b90b" />
      </linearGradient>
    </defs>
  );
}

function Floor() {
  return (
    <>
      {/* Floor base — coords are front-office-local since rendered inside
          the translate(0, FRONT_OFFICE_Y) group. */}
      <rect
        x="0"
        y={BACK_WALL_H}
        width={VIEW_W}
        height={FRONT_OFFICE_HEIGHT - BACK_WALL_H}
        fill="url(#floorTile)"
      />
      {/* Subtle floor reflection band under counter */}
      <rect
        x="0"
        y={COUNTER_Y + COUNTER_H}
        width={VIEW_W}
        height="40"
        fill="url(#floorGrad)"
        opacity="0.4"
      />
    </>
  );
}

function FloorLights() {
  return (
    <>
      {[150, 400, 650].map((x) => (
        <circle key={x} cx={x} cy="320" r="120" fill="url(#lightGrad)" />
      ))}
    </>
  );
}

function BackWall() {
  return (
    <>
      <rect x="0" y="0" width={VIEW_W} height={BACK_WALL_H} fill="url(#wallGrad)" />
      <line x1="0" y1={BACK_WALL_H} x2={VIEW_W} y2={BACK_WALL_H} stroke="#0a0d12" strokeWidth="2" />
      {/* Subtle wall texture lines */}
      <line x1="0" y1={BACK_WALL_H - 1} x2={VIEW_W} y2={BACK_WALL_H - 1} stroke="rgba(255,255,255,0.04)" />
    </>
  );
}

function RateBoard() {
  return (
    <g transform={`translate(${VIEW_W / 2 - 130}, 12)`}>
      <rect width="260" height="48" rx="3" fill="#06080c" stroke="#2a3140" strokeWidth="1" />
      <rect x="2" y="2" width="256" height="3" fill="rgba(255,255,255,0.05)" rx="1" />
      <text x="14" y="20" fill="#0ecb81" fontSize="11" fontFamily="monospace" fontWeight="700">
        BTC ↑ 67,841
      </text>
      <text x="14" y="36" fill="#f6465d" fontSize="11" fontFamily="monospace" fontWeight="700">
        ETH ↓ 3,142
      </text>
      <text x="140" y="20" fill="#f0b90b" fontSize="11" fontFamily="monospace" fontWeight="700">
        TON ↑ 5.42
      </text>
      <text x="140" y="36" fill="#0ecb81" fontSize="11" fontFamily="monospace" fontWeight="700">
        USDT 1.0001
      </text>
      {/* Tiny blinking LED */}
      <motion.circle
        cx="250"
        cy="9"
        r="2"
        fill="#0ecb81"
        animate={{ opacity: [1, 0.3, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </g>
  );
}

function Counter() {
  return (
    <g>
      {/* Counter front face (visible bottom edge) */}
      <rect
        x="40"
        y={COUNTER_Y}
        width={VIEW_W - 80}
        height={COUNTER_H}
        fill="url(#counterGrad)"
        stroke="#1a1208"
        strokeWidth="0.5"
      />
      {/* Top surface highlight */}
      <rect x="40" y={COUNTER_Y} width={VIEW_W - 80} height="3" fill="url(#counterTopGrad)" />
      {/* Subtle wood grain */}
      {[0, 1, 2].map((i) => (
        <line
          key={i}
          x1="40"
          y1={COUNTER_Y + 6 + i * 5}
          x2={VIEW_W - 40}
          y2={COUNTER_Y + 6 + i * 5}
          stroke="rgba(255,220,180,0.04)"
          strokeWidth="0.5"
        />
      ))}
      {/* Drop shadow under counter */}
      <rect
        x="40"
        y={COUNTER_Y + COUNTER_H}
        width={VIEW_W - 80}
        height="6"
        fill="rgba(0,0,0,0.5)"
        opacity="0.6"
      />
    </g>
  );
}

function Glass() {
  return (
    <g>
      {/* Glass partition (semi-transparent) */}
      <rect
        x="40"
        y={GLASS_Y_TOP}
        width={VIEW_W - 80}
        height={GLASS_H}
        fill="url(#glassGrad)"
        stroke="rgba(180,210,240,0.35)"
        strokeWidth="0.7"
      />
      {/* Glass diagonal reflections */}
      <line
        x1="80"
        y1={GLASS_Y_TOP + GLASS_H}
        x2="180"
        y2={GLASS_Y_TOP}
        stroke="rgba(255,255,255,0.07)"
        strokeWidth="2"
      />
      <line
        x1="240"
        y1={GLASS_Y_TOP + GLASS_H}
        x2="320"
        y2={GLASS_Y_TOP}
        stroke="rgba(255,255,255,0.05)"
        strokeWidth="1.5"
      />
      <line
        x1="540"
        y1={GLASS_Y_TOP + GLASS_H}
        x2="640"
        y2={GLASS_Y_TOP}
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="2"
      />
      {/* Glass top frame */}
      <rect x="40" y={GLASS_Y_TOP - 2} width={VIEW_W - 80} height="2" fill="#3a4150" />
    </g>
  );
}

function CounterWindows({ count }: { count: number }) {
  // Service window cutouts: small dark slot in the counter where money exchanges
  return (
    <g>
      {WINDOW_X.slice(0, count).map((x) => (
        <g key={x}>
          {/* Cash tray (rectangular indent on the counter) */}
          <rect
            x={x - 28}
            y={COUNTER_Y + 5}
            width="56"
            height="10"
            fill="#1a1208"
            rx="1"
          />
          <rect
            x={x - 26}
            y={COUNTER_Y + 6}
            width="52"
            height="2"
            fill="rgba(0,0,0,0.6)"
          />
          {/* Microphone speaker indicator on glass */}
          <circle cx={x} cy={GLASS_Y_TOP + 16} r="3" fill="#2a3140" stroke="#3a4150" strokeWidth="0.5" />
          <circle cx={x} cy={GLASS_Y_TOP + 16} r="1" fill="#0a0d12" />
        </g>
      ))}
    </g>
  );
}

function TellerStation({ x, type, level }: { x: number; type: string; level: number }) {
  const color = STAFF_COLOR[type] ?? STAFF_COLOR.CASHIER;
  const label = STAFF_LABEL[type]?.[0] ?? "?";

  return (
    <g>
      {/* Desk (behind teller, between back wall and counter) */}
      <rect
        x={x - 60}
        y={STAFF_DESK_Y}
        width="120"
        height="22"
        fill="#2a2f38"
        stroke="rgba(0,0,0,0.4)"
        strokeWidth="0.5"
        rx="1"
      />
      <rect
        x={x - 60}
        y={STAFF_DESK_Y}
        width="120"
        height="2"
        fill="rgba(255,255,255,0.06)"
      />
      {/* Monitor on desk */}
      <rect x={x - 16} y={STAFF_DESK_Y - 6} width="32" height="10" fill="#0a0d12" stroke="#3a4150" strokeWidth="0.5" rx="1" />
      <rect x={x - 14} y={STAFF_DESK_Y - 5} width="28" height="8" fill="url(#monitorGlow)" rx="0.5" />
      {/* Keyboard */}
      <rect x={x - 18} y={STAFF_DESK_Y + 8} width="36" height="6" fill="#1a1f29" rx="0.5" />
      {/* Cup of coffee */}
      <circle cx={x + 26} cy={STAFF_DESK_Y + 11} r="3" fill="#3a2818" stroke="#1a0e08" strokeWidth="0.5" />

      {/* Office chair (top-down: armrests + back) */}
      <g>
        {/* Chair back */}
        <rect
          x={x - 16}
          y={STAFF_CHAIR_Y - 4}
          width="32"
          height="6"
          fill="#1a1f29"
          rx="2"
        />
        <rect
          x={x - 16}
          y={STAFF_CHAIR_Y - 4}
          width="32"
          height="2"
          fill={color.suit}
          opacity={Math.min(0.4 + level * 0.15, 0.95)}
          rx="2"
        />
        {/* Armrests */}
        <rect x={x - 22} y={STAFF_CHAIR_Y + 2} width="4" height="14" fill="#1a1f29" rx="1" />
        <rect x={x + 18} y={STAFF_CHAIR_Y + 2} width="4" height="14" fill="#1a1f29" rx="1" />
        {/* Chair seat */}
        <rect x={x - 18} y={STAFF_CHAIR_Y + 2} width="36" height="14" fill="#2a2f38" rx="2" />
      </g>

      {/* Staff sprite — sitting at chair, head visible above counter slightly */}
      <g transform={`translate(${x}, ${STAFF_HEAD_Y})`}>
        <RoleCharacter type={type} level={level} />
      </g>

      {/* Level + role tag below counter (on glass face) */}
      <g transform={`translate(${x - 18}, ${COUNTER_Y + COUNTER_H + 12})`}>
        <rect width="36" height="13" rx="6" fill="rgba(0,0,0,0.6)" stroke={color.suit} strokeWidth="0.5" />
        <text x="18" y="9" textAnchor="middle" fontSize="9" fontWeight="700" fill={color.suit}>
          {label}·L{level}
        </text>
      </g>
    </g>
  );
}

function CornerPlant({ x, y, flip = false }: { x: number; y: number; flip?: boolean }) {
  const dir = flip ? -1 : 1;
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Pot */}
      <path
        d="M -14 8 L -12 26 L 12 26 L 14 8 Z"
        fill="#3a2418"
        stroke="rgba(0,0,0,0.4)"
        strokeWidth="0.5"
      />
      <ellipse cx="0" cy="8" rx="14" ry="4" fill="#5a3624" />
      <ellipse cx="0" cy="8" rx="11" ry="2.5" fill="#2a1808" />
      {/* Foliage clusters */}
      <ellipse cx={dir * -4} cy="-2" rx="10" ry="8" fill="#1a3a1a" />
      <ellipse cx={dir * 6} cy="-4" rx="9" ry="9" fill="#264a26" />
      <ellipse cx={dir * 0} cy="-10" rx="8" ry="9" fill="#2e5a2e" />
      <ellipse cx={dir * -6} cy="-6" rx="6" ry="7" fill="#3a6a3a" />
      <ellipse cx={dir * 4} cy="-8" rx="5" ry="6" fill="#4a7a4a" />
      {/* Tiny highlight leaves */}
      <ellipse cx={dir * 2} cy="-12" rx="2" ry="3" fill="#5a8a5a" />
      <ellipse cx={dir * -2} cy="-9" rx="1.5" ry="2.5" fill="#5a8a5a" />
    </g>
  );
}

function Bench({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Bench shadow */}
      <rect x="-2" y="36" width="184" height="6" fill="rgba(0,0,0,0.4)" rx="3" />
      {/* Backrest */}
      <rect x="0" y="-12" width="180" height="14" fill="#3a2818" stroke="rgba(0,0,0,0.4)" strokeWidth="0.5" rx="2" />
      <rect x="0" y="-12" width="180" height="3" fill="rgba(255,180,120,0.15)" rx="2" />
      {/* Seat cushion */}
      <rect x="0" y="2" width="180" height="36" fill="#4a342a" stroke="rgba(0,0,0,0.4)" strokeWidth="0.5" rx="3" />
      <rect x="2" y="4" width="176" height="3" fill="rgba(255,180,120,0.12)" rx="2" />
      {/* Seat dividers */}
      <line x1="60" y1="2" x2="60" y2="38" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" />
      <line x1="120" y1="2" x2="120" y2="38" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" />
      {/* Subtle cushion stitching */}
      <ellipse cx="30" cy="20" rx="22" ry="14" fill="rgba(255,255,255,0.02)" />
      <ellipse cx="90" cy="20" rx="22" ry="14" fill="rgba(255,255,255,0.02)" />
      <ellipse cx="150" cy="20" rx="22" ry="14" fill="rgba(255,255,255,0.02)" />
    </g>
  );
}

function Atm({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Shadow */}
      <rect x="-2" y="74" width="68" height="6" fill="rgba(0,0,0,0.5)" rx="2" />
      {/* Body */}
      <rect width="64" height="76" fill="#2a3140" stroke="#0a0d12" strokeWidth="1" rx="3" />
      <rect width="64" height="3" fill="rgba(255,255,255,0.06)" rx="3" />
      {/* Screen */}
      <rect x="6" y="8" width="52" height="30" fill="#06080c" stroke="#3a4150" strokeWidth="0.7" rx="1.5" />
      <rect x="8" y="10" width="48" height="26" fill="url(#monitorGlow)" rx="0.5" />
      <text x="32" y="22" textAnchor="middle" fontSize="6" fill="#0ecb81" fontFamily="monospace">
        INSERT
      </text>
      <text x="32" y="30" textAnchor="middle" fontSize="6" fill="#0ecb81" fontFamily="monospace">
        CARD
      </text>
      {/* Card slot */}
      <rect x="12" y="44" width="22" height="3" fill="#0a0d12" rx="0.5" />
      {/* Cash dispenser slot */}
      <rect x="38" y="44" width="20" height="6" fill="#0a0d12" rx="1" />
      {/* Keypad (3x4 dots) */}
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <circle
            key={`${row}-${col}`}
            cx={14 + col * 8}
            cy={56 + row * 6}
            r="1.5"
            fill="#3a4150"
          />
        ))
      )}
      {/* Brand logo */}
      <text x="32" y="74" textAnchor="middle" fontSize="7" fontWeight="700" fill="#f0b90b">
        STELLEX
      </text>
    </g>
  );
}

function Entrance({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Door mat */}
      <rect x="-50" y="-22" width="100" height="14" fill="#1a1f29" stroke="rgba(240,185,11,0.2)" strokeWidth="1" rx="2" />
      <rect x="-44" y="-19" width="88" height="2" fill="rgba(240,185,11,0.15)" />
      <rect x="-44" y="-13" width="88" height="2" fill="rgba(240,185,11,0.15)" />
      {/* "ENTRANCE" label */}
      <text x="0" y="6" textAnchor="middle" fontSize="9" fontWeight="700" fill="#848e9c" letterSpacing="2">
        ENTRANCE
      </text>
      {/* Arrow indicator */}
      <path d="M -6 12 L 0 18 L 6 12" stroke="#848e9c" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </g>
  );
}

function SecurityCam({ x, y, flip = false }: { x: number; y: number; flip?: boolean }) {
  const dir = flip ? -1 : 1;
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Mount */}
      <rect x="-3" y="0" width="6" height="6" fill="#1a1f29" />
      {/* Camera body */}
      <ellipse cx={dir * 6} cy="10" rx="9" ry="6" fill="#2a3140" stroke="#0a0d12" strokeWidth="0.5" />
      {/* Lens */}
      <circle cx={dir * 10} cy="11" r="3" fill="#06080c" stroke="#3a4150" strokeWidth="0.5" />
      <circle cx={dir * 10} cy="11" r="1.5" fill="#1a4a6a" />
      {/* Recording LED */}
      <motion.circle
        cx={dir * 4}
        cy="9"
        r="0.8"
        fill="#f6465d"
        animate={{ opacity: [1, 0.2, 1] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
    </g>
  );
}

function ClosedSign() {
  return (
    <g transform={`translate(${VIEW_W / 2}, ${VIEW_H / 2 + 30})`}>
      <rect x="-90" y="-18" width="180" height="36" rx="18" fill="rgba(0,0,0,0.7)" stroke="#2a3140" strokeWidth="1" />
      <text x="0" y="6" textAnchor="middle" fontSize="13" fontWeight="700" fill="#848e9c" letterSpacing="2">
        HIRE STAFF TO OPEN
      </text>
    </g>
  );
}

// ─── Animated agents ─────────────────────────────────────────────────────

function Customer({
  customerId,
  windowIdx,
  variant,
  onServed,
  onCompleted,
}: {
  customerId: string;
  windowIdx: number;
  variant: number;
  onServed: (windowIdx: number) => void;
  onCompleted: (customerId: string) => void;
}) {
  const [phase, setPhase] = useState<"approach" | "serving" | "leave">("approach");
  const colors = CUSTOMER_VARIANTS[variant] ?? CUSTOMER_VARIANTS[0];
  const targetX = WINDOW_X[windowIdx];

  useEffect(() => {
    if (phase === "approach") {
      // Approach + leave timings bumped 1.5x to match the slower motion
      // duration below — otherwise the customer's phase advances before
      // their animation has reached the window.
      const t = window.setTimeout(() => {
        onServed(windowIdx);
        setPhase("serving");
      }, 1425);
      return () => window.clearTimeout(t);
    }
    if (phase === "serving") {
      // Serving is stationary — keep the original idle.
      const t = window.setTimeout(() => setPhase("leave"), 750);
      return () => window.clearTimeout(t);
    }
    if (phase === "leave") {
      const t = window.setTimeout(() => onCompleted(customerId), 1275);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [phase, windowIdx, customerId, onServed, onCompleted]);

  // Translate-based animation. Initial position is at the entrance, target
  // depends on phase.
  const animate =
    phase === "leave"
      ? { x: ENTRANCE_X, y: ENTRANCE_Y, opacity: 0 }
      : { x: targetX, y: SERVE_Y, opacity: 1 };

  return (
    <motion.g
      initial={{ x: ENTRANCE_X, y: ENTRANCE_Y, opacity: 0 }}
      animate={animate}
      exit={{ opacity: 0 }}
      // 0.85s → 1.275s walks: customers no longer dart across the lobby
      // — they actually feel like people approaching a counter.
      transition={{ duration: 1.275, ease: "easeInOut" }}
    >
      {/* Inner group at 1.5× — bumps the whole customer (head, body, bag,
       * shadow) without recomputing every dimension. The outer motion.g
       * still controls x/y/opacity. */}
      <g transform="scale(1.5)">
        {/* Shadow */}
        <ellipse cx="0" cy="14" rx="11" ry="3" fill="rgba(0,0,0,0.45)" />
        {/* Coat / shoulders */}
        <ellipse cx="0" cy="4" rx="11" ry="8" fill={colors.coat} />
        <ellipse cx="0" cy="4" rx="11" ry="8" fill="rgba(255,255,255,0.04)" />
        {/* Bag/briefcase (off to the side) */}
        <rect x="9" y="3" width="5" height="6" fill="#1a1208" rx="0.5" />
        <rect x="9.5" y="3.5" width="4" height="1" fill="#3a2818" rx="0.3" />
        {/* Head */}
        <circle cx="0" cy="-3" r="5.5" fill={colors.head} stroke="rgba(0,0,0,0.3)" strokeWidth="0.4" />
        {/* Hair / beanie */}
        <path d={`M -4.5 -6 Q 0 -10 4.5 -6`} fill="#1a0d08" opacity="0.85" />
        {/* Subtle "thinking" pulse during serving */}
        {phase === "serving" && (
          <motion.circle
            cx="0"
            cy="-12"
            r="1.5"
            fill="#f0b90b"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.8, repeat: 1 }}
          />
        )}
      </g>
    </motion.g>
  );
}

function Floaty({ x, y, amount }: CoinFloaty) {
  return (
    <motion.g
      initial={{ opacity: 0, y: y, scale: 0.5 }}
      animate={{ opacity: 1, y: y - 60, scale: 1 }}
      exit={{ opacity: 0, y: y - 90 }}
      transition={{ duration: 1.1, ease: "easeOut" }}
      style={{ pointerEvents: "none" }}
    >
      {/* Coin glow */}
      <circle cx={x} cy="0" r="9" fill="rgba(240,185,11,0.25)" filter="url(#softShadow)" />
      <circle cx={x} cy="0" r="6.5" fill="#f0b90b" stroke="#bf8a08" strokeWidth="0.7" />
      <text
        x={x}
        y="2"
        textAnchor="middle"
        fontSize="6"
        fontWeight="700"
        fill="#3a2a08"
      >
        $
      </text>
      <text
        x={x + 12}
        y="3"
        fontSize="11"
        fontWeight="700"
        fill="#f0b90b"
        style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))" }}
      >
        +{amount.toLocaleString()}
      </text>
    </motion.g>
  );
}

// ─── Back office ─────────────────────────────────────────────────────

function Hallway() {
  return (
    <g transform={`translate(0, ${HALLWAY_Y})`}>
      <rect width={VIEW_W} height={HALLWAY_H} fill="#06080c" />
      {/* Top + bottom wall edges */}
      <line x1="0" y1="0" x2={VIEW_W} y2="0" stroke="#000" strokeWidth="1.5" />
      <line x1="0" y1={HALLWAY_H} x2={VIEW_W} y2={HALLWAY_H} stroke="#000" strokeWidth="1.5" />
      {/* Gold accent strip with dashed line — corridor floor marking */}
      <line
        x1="40"
        y1={HALLWAY_H / 2}
        x2={VIEW_W - 40}
        y2={HALLWAY_H / 2}
        stroke="rgba(240,185,11,0.3)"
        strokeWidth="0.5"
        strokeDasharray="6 8"
      />
      <text
        x={VIEW_W / 2}
        y={HALLWAY_H / 2 + 3}
        textAnchor="middle"
        fontSize="6"
        fill="#5a6470"
        letterSpacing="3"
      >
        STAFF CORRIDOR
      </text>
    </g>
  );
}

function BackOffice({
  branchId,
  managers,
  marketers,
  guards,
  traders,
  quants,
  onHire,
  onUpgrade,
  busy,
}: {
  branchId: string;
  managers: StaffMember[];
  marketers: StaffMember[];
  guards: StaffMember[];
  traders: StaffMember[];
  quants: StaffMember[];
  onHire?: (branchId: string, role: BackOfficeRole) => void;
  onUpgrade?: (staffId: string, currentLevel: number) => void;
  busy: boolean;
}) {
  const fireHire = (role: BackOfficeRole) => () => {
    if (!busy) onHire?.(branchId, role);
  };
  const fireUpgrade = (s: StaffMember) => () => {
    if (!busy) onUpgrade?.(s.id, s.level);
  };
  return (
    <g>
      {/* Floor */}
      <rect x="0" y="0" width={VIEW_W} height={BACK_OFFICE_HEIGHT} fill="url(#backOfficeTile)" />

      {/* Title bar across the top */}
      <rect x="0" y="0" width={VIEW_W} height={BACK_OFFICE_TITLE_H} fill="#1c2230" />
      <line x1="0" y1={BACK_OFFICE_TITLE_H} x2={VIEW_W} y2={BACK_OFFICE_TITLE_H} stroke="#000" strokeWidth="1" />
      <text
        x={VIEW_W / 2}
        y={16}
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        fill="#848e9c"
        letterSpacing="3"
      >
        BACK OFFICE
      </text>

      {/* Zone tints — subtle wash to distinguish rooms */}
      <rect
        x={SECURITY_ZONE.x}
        y={BACK_OFFICE_TITLE_H}
        width={SECURITY_ZONE.w}
        height={BACK_OFFICE_HEIGHT - BACK_OFFICE_TITLE_H}
        fill="rgba(31,80,120,0.08)"
      />
      <rect
        x={TRADE_HUB_ZONE.x}
        y={BACK_OFFICE_TITLE_H}
        width={TRADE_HUB_ZONE.w}
        height={BACK_OFFICE_HEIGHT - BACK_OFFICE_TITLE_H}
        fill="rgba(31,138,79,0.07)"
      />
      <rect
        x={MANAGER_ZONE.x}
        y={BACK_OFFICE_TITLE_H}
        width={MANAGER_ZONE.w}
        height={BACK_OFFICE_HEIGHT - BACK_OFFICE_TITLE_H}
        fill="rgba(220,140,40,0.06)"
      />

      {/* Zone divider walls */}
      <line
        x1={TRADE_HUB_ZONE.x}
        y1={BACK_OFFICE_TITLE_H}
        x2={TRADE_HUB_ZONE.x}
        y2={BACK_OFFICE_HEIGHT}
        stroke="rgba(0,0,0,0.6)"
        strokeWidth="2"
      />
      <line
        x1={MANAGER_ZONE.x}
        y1={BACK_OFFICE_TITLE_H}
        x2={MANAGER_ZONE.x}
        y2={BACK_OFFICE_HEIGHT}
        stroke="rgba(0,0,0,0.6)"
        strokeWidth="2"
      />

      <SecurityStation
        guards={guards}
        onHireSeat={fireHire("SECURITY")}
        onUpgradeSeat={fireUpgrade}
        busy={busy}
      />
      <TradeHub
        traders={traders}
        quants={quants}
        onHireTrader={fireHire("TRADER")}
        onHireQuant={fireHire("QUANT")}
        onUpgradeSeat={fireUpgrade}
        busy={busy}
      />
      <ManagerOffice
        managers={managers}
        marketers={marketers}
        onHireManager={fireHire("MANAGER")}
        onHireMarketing={fireHire("MARKETING")}
        onUpgradeSeat={fireUpgrade}
        busy={busy}
      />

      {/* Always-present clipboard courier looping the room — sells the
          "alive office" feeling regardless of which support staff are
          hired. Pure cosmetic. */}
      <BackOfficeCourier />
    </g>
  );
}

// ─── Generic interactive seat overlay ─────────────────────────────────

interface SeatHotzoneProps {
  cx: number;
  cy: number;
  w: number;
  h: number;
  filled: boolean;
  busy: boolean;
  accent: string;
  label: string;
  onClick: () => void;
}

/**
 * Tap target on top of a desk. Renders an outlined ring on hover/active so
 * the player learns the affordance. Empty seats get a "+" hint, occupied
 * seats get an "↑" upgrade hint. Used by all three back-office zones.
 */
function SeatHotzone({
  cx,
  cy,
  w,
  h,
  filled,
  busy,
  accent,
  label,
  onClick,
}: SeatHotzoneProps) {
  const half = { w: w / 2, h: h / 2 };
  return (
    <g
      className={`back-office-seat ${filled ? "filled" : "empty"} ${busy ? "busy" : ""}`}
      onClick={busy ? undefined : onClick}
      style={{ cursor: busy ? "not-allowed" : "pointer" }}
    >
      {/* Invisible click target */}
      <rect
        x={cx - half.w}
        y={cy - half.h}
        width={w}
        height={h}
        fill="transparent"
      />
      {/* Hover/focus ring */}
      <rect
        className="seat-ring"
        x={cx - half.w + 1}
        y={cy - half.h + 1}
        width={w - 2}
        height={h - 2}
        fill="none"
        stroke={accent}
        strokeWidth="1.2"
        strokeDasharray="3 3"
        rx="6"
        opacity="0"
      />
      {/* Action hint badge — visible on the empty state always, on
          occupied only on hover (CSS). */}
      <g
        className="seat-hint"
        transform={`translate(${cx + half.w - 14}, ${cy - half.h + 8})`}
      >
        <circle r="7" fill={accent} stroke="#000" strokeWidth="0.5" />
        <text
          x="0"
          y="3"
          textAnchor="middle"
          fontSize="10"
          fontWeight="700"
          fill="#000"
        >
          {label}
        </text>
      </g>
    </g>
  );
}

// ─── Security zone ───────────────────────────────────────────────────

function SecurityStation({
  guards,
  onHireSeat,
  onUpgradeSeat,
  busy,
}: {
  guards: StaffMember[];
  onHireSeat: () => void;
  onUpgradeSeat: (s: StaffMember) => () => void;
  busy: boolean;
}) {
  const baseX = SECURITY_ZONE.x;
  const w = SECURITY_ZONE.w;
  const cx = baseX + w / 2;
  return (
    <g>
      {/* Zone label + room name */}
      <text
        x={cx}
        y={BACK_OFFICE_TITLE_H + 14}
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill="#7eaaff"
        letterSpacing="2"
      >
        SECURITY
      </text>

      {/* CCTV monitor wall */}
      <CctvMonitorWall x={baseX + 14} y={BACK_OFFICE_TITLE_H + 24} w={w - 28} h={50} />

      {/* Two security desks side-by-side, facing the monitor wall */}
      <SecurityDesk x={baseX + 70} y={BACK_OFFICE_TITLE_H + 100} guard={guards[0]} />
      <SecurityDesk x={baseX + 180} y={BACK_OFFICE_TITLE_H + 100} guard={guards[1]} />

      {/* Tap-to-hire / tap-to-upgrade overlays */}
      {[
        { x: baseX + 70, guard: guards[0] },
        { x: baseX + 180, guard: guards[1] },
      ].map((slot, i) => (
        <SeatHotzone
          key={i}
          cx={slot.x}
          cy={BACK_OFFICE_TITLE_H + 130}
          w={70}
          h={80}
          filled={Boolean(slot.guard)}
          busy={busy}
          accent="#7eaaff"
          label={slot.guard ? "↑" : "+"}
          onClick={slot.guard ? onUpgradeSeat(slot.guard) : onHireSeat}
        />
      ))}

      {/* Locker / rack at the bottom corner */}
      <SecurityLocker x={baseX + 14} y={BACK_OFFICE_HEIGHT - 50} />

      {/* Live shield emblem — pulses brighter as more / higher-level
          security guards are on duty. */}
      <SecurityShieldPulse
        x={baseX + w - 36}
        y={BACK_OFFICE_HEIGHT - 50}
        intensity={guards.reduce((s, g) => s + g.level, 0)}
      />
    </g>
  );
}

function CctvMonitorWall({
  x,
  y,
  w,
  h,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  const cells = 4;
  const cellW = w / cells;
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Wall mount frame */}
      <rect width={w} height={h} fill="#0a0d12" stroke="#2a3140" strokeWidth="1" rx="2" />
      <rect x="2" y="2" width={w - 4} height="2" fill="rgba(255,255,255,0.05)" rx="1" />
      {Array.from({ length: cells }).map((_, i) => {
        const mx = 4 + i * cellW;
        return (
          <g key={i}>
            <rect
              x={mx}
              y="6"
              width={cellW - 4}
              height={h - 14}
              fill="url(#cctvFeed)"
              stroke="#1a2a3a"
              strokeWidth="0.5"
              rx="1"
            />
            {/* Subtle scan line */}
            <line
              x1={mx + 2}
              y1={h - 14}
              x2={mx + cellW - 6}
              y2={h - 14}
              stroke="rgba(0,200,100,0.2)"
              strokeWidth="0.5"
            />
            {/* Camera ID */}
            <text
              x={mx + 3}
              y={h - 4}
              fontSize="5"
              fill="#3a8a4a"
              fontFamily="monospace"
            >
              CAM 0{i + 1}
            </text>
            {/* Recording LED */}
            <motion.circle
              cx={mx + cellW - 8}
              cy={h - 4}
              r="1.2"
              fill="#f6465d"
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.35,
              }}
            />
            {/* Faux camera-feed silhouette */}
            <rect x={mx + 3} y="8" width={cellW - 10} height={h - 24} fill="rgba(0,0,0,0.4)" rx="0.5" />
            <circle cx={mx + cellW / 2} cy={h / 2 - 1} r="1.8" fill="#1a4a2a" opacity="0.6" />
          </g>
        );
      })}
    </g>
  );
}

function SecurityDesk({
  x,
  y,
  guard,
}: {
  x: number;
  y: number;
  guard?: StaffMember;
}) {
  return (
    <g>
      {/* Desk surface */}
      <rect x={x - 32} y={y} width="64" height="20" fill="#2a2f38" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="1.5" />
      <rect x={x - 32} y={y} width="64" height="2" fill="rgba(255,255,255,0.06)" />
      {/* Small monitor */}
      <rect x={x - 14} y={y - 6} width="28" height="11" fill="#0a0d12" stroke="#3a4150" strokeWidth="0.5" rx="1" />
      <rect x={x - 12} y={y - 5} width="24" height="9" fill="url(#monitorGlow)" rx="0.5" />
      {/* Walkie-talkie */}
      <rect x={x - 30} y={y + 4} width="6" height="11" fill="#1a1f29" rx="1" />
      <circle cx={x - 27} cy={y + 7} r="0.9" fill="#3a4150" />
      <line x1={x - 27} y1={y + 4} x2={x - 27} y2={y + 1} stroke="#3a4150" strokeWidth="0.6" />
      {/* Coffee mug */}
      <circle cx={x + 24} cy={y + 11} r="3" fill="#3a2818" stroke="#1a0e08" strokeWidth="0.5" />
      <rect x={x + 26} y={y + 9} width="2" height="3" fill="#3a2818" rx="0.5" />

      {/* Office chair (south of desk; guard faces north toward wall) */}
      <rect x={x - 16} y={y + 24} width="32" height="6" fill="#1a1f29" rx="2" />
      <rect x={x - 16} y={y + 28} width="32" height="2" fill="#1f2937" rx="2" />
      <rect x={x - 18} y={y + 30} width="36" height="14" fill="#2a2f38" rx="2" />
      <rect x={x - 22} y={y + 32} width="4" height="10" fill="#1a1f29" rx="1" />
      <rect x={x + 18} y={y + 32} width="4" height="10" fill="#1a1f29" rx="1" />

      {guard ? (
        <BackOfficeWorker
          x={x}
          y={y + 36}
          type="SECURITY"
          level={guard.level}
        />
      ) : (
        <text
          x={x}
          y={y + 40}
          textAnchor="middle"
          fontSize="6"
          fill="rgba(126,170,255,0.4)"
          letterSpacing="1"
        >
          VACANT
        </text>
      )}
    </g>
  );
}

function SecurityLocker({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect width="46" height="40" fill="#1a1f29" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="2" />
      <rect width="46" height="3" fill="rgba(255,255,255,0.05)" rx="2" />
      {/* 3 locker doors */}
      <line x1="15.3" y1="3" x2="15.3" y2="38" stroke="rgba(0,0,0,0.6)" strokeWidth="0.5" />
      <line x1="30.6" y1="3" x2="30.6" y2="38" stroke="rgba(0,0,0,0.6)" strokeWidth="0.5" />
      {/* Vents at top of each door */}
      {[7.5, 22.8, 38].map((cx) => (
        <g key={cx}>
          <line x1={cx - 3} y1="6" x2={cx + 3} y2="6" stroke="rgba(126,170,255,0.4)" strokeWidth="0.4" />
          <line x1={cx - 3} y1="9" x2={cx + 3} y2="9" stroke="rgba(126,170,255,0.4)" strokeWidth="0.4" />
          <circle cx={cx} cy="22" r="0.9" fill="#3a4150" />
        </g>
      ))}
      <text x="23" y="48" textAnchor="middle" fontSize="6" fill="#5a6470" letterSpacing="1">
        LOCKERS
      </text>
    </g>
  );
}

// ─── Marketing zone ──────────────────────────────────────────────────

function TradeHub({
  traders,
  quants,
  onHireTrader,
  onHireQuant,
  onUpgradeSeat,
  busy,
}: {
  traders: StaffMember[];
  quants: StaffMember[];
  onHireTrader: () => void;
  onHireQuant: () => void;
  onUpgradeSeat: (s: StaffMember) => () => void;
  busy: boolean;
}) {
  const baseX = TRADE_HUB_ZONE.x;
  const w = TRADE_HUB_ZONE.w;
  const cx = baseX + w / 2;

  // 3 desks: 2 traders (slots 0-1) + 1 quant (slot 2). Layout matches the
  // old marketing pod — same x positions so existing furniture (whiteboard,
  // filing cabinet, plant) still fits cleanly.
  const seats: Array<{ type: "TRADER" | "QUANT"; staff: StaffMember | undefined }> = [
    { type: "TRADER", staff: traders[0] },
    { type: "TRADER", staff: traders[1] },
    { type: "QUANT", staff: quants[0] },
  ];

  return (
    <g>
      <text
        x={cx}
        y={BACK_OFFICE_TITLE_H + 14}
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill="#1f8a4f"
        letterSpacing="2"
      >
        TRADE HUB
      </text>

      {/* Reuse the whiteboard frame as a "market data" display — same
       * shape, the squiggly marker lines already read as stock charts. */}
      <Whiteboard x={baseX + 16} y={BACK_OFFICE_TITLE_H + 24} w={w - 32} h={48} />

      {/* 3 trader/quant desks in a row */}
      {seats.map((seat, i) => {
        const dx = baseX + 50 + i * 70;
        return (
          <MarketingDesk
            key={i}
            x={dx}
            y={BACK_OFFICE_TITLE_H + 100}
            marketer={seat.staff}
          />
        );
      })}

      {/* Tap-to-act overlays — separate hire handler per role so the right
       * staff type is created for each seat. */}
      {seats.map((seat, i) => {
        const dx = baseX + 50 + i * 70;
        const accent = seat.type === "TRADER" ? "#1f8a4f" : "#7c3aed";
        return (
          <SeatHotzone
            key={i}
            cx={dx}
            cy={BACK_OFFICE_TITLE_H + 130}
            w={56}
            h={80}
            filled={Boolean(seat.staff)}
            busy={busy}
            accent={accent}
            label={seat.staff ? "↑" : "+"}
            onClick={
              seat.staff
                ? onUpgradeSeat(seat.staff)
                : seat.type === "TRADER"
                  ? onHireTrader
                  : onHireQuant
            }
          />
        );
      })}

      {/* Filing cabinet bottom-left */}
      <FilingCabinet x={baseX + 16} y={BACK_OFFICE_HEIGHT - 50} />
      {/* Plant bottom-right */}
      <SmallPlant x={baseX + w - 26} y={BACK_OFFICE_HEIGHT - 36} />
    </g>
  );
}

function Whiteboard({
  x,
  y,
  w,
  h,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Frame */}
      <rect width={w} height={h} fill="#3a4150" rx="1.5" />
      <rect x="2" y="2" width={w - 4} height={h - 4} fill="url(#whiteboardGrad)" rx="1" />
      {/* Faint marker squiggles */}
      <path
        d={`M 6 ${h * 0.4} Q ${w * 0.3} ${h * 0.2}, ${w * 0.45} ${h * 0.5} T ${w * 0.7} ${h * 0.4}`}
        stroke="#1a4a8a"
        strokeWidth="0.6"
        fill="none"
        opacity="0.4"
      />
      <path
        d={`M ${w * 0.5} ${h * 0.65} L ${w * 0.7} ${h * 0.55} L ${w * 0.85} ${h * 0.7}`}
        stroke="#a8324a"
        strokeWidth="0.6"
        fill="none"
        opacity="0.4"
      />
      {/* Sticky notes — colorful little squares */}
      {[
        { x: 8, y: 6, c: "#ffe082" },
        { x: 22, y: 8, c: "#ffab91" },
        { x: 36, y: 6, c: "#a5d6a7" },
        { x: 50, y: 10, c: "#90caf9" },
        { x: w - 28, y: 6, c: "#f48fb1" },
        { x: w - 14, y: 9, c: "#ffe082" },
      ].map((n, i) => (
        <g key={i}>
          <rect
            x={n.x}
            y={n.y}
            width="9"
            height="9"
            fill={n.c}
            stroke="rgba(0,0,0,0.15)"
            strokeWidth="0.3"
          />
          <line
            x1={n.x + 1}
            y1={n.y + 3}
            x2={n.x + 7}
            y2={n.y + 3}
            stroke="rgba(0,0,0,0.2)"
            strokeWidth="0.3"
          />
          <line
            x1={n.x + 1}
            y1={n.y + 5}
            x2={n.x + 6}
            y2={n.y + 5}
            stroke="rgba(0,0,0,0.2)"
            strokeWidth="0.3"
          />
        </g>
      ))}
      {/* Marker tray on bottom */}
      <rect x="2" y={h - 4} width={w - 4} height="2" fill="#5a6370" />
      <rect x="6" y={h - 3.5} width="3" height="1" fill="#f6465d" />
      <rect x="11" y={h - 3.5} width="3" height="1" fill="#1f6feb" />
      <rect x="16" y={h - 3.5} width="3" height="1" fill="#0ecb81" />
    </g>
  );
}

function MarketingDesk({
  x,
  y,
  marketer,
}: {
  x: number;
  y: number;
  marketer?: StaffMember;
}) {
  return (
    <g>
      {/* Desk */}
      <rect x={x - 26} y={y} width="52" height="22" fill="#2a2f38" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="1.5" />
      <rect x={x - 26} y={y} width="52" height="2" fill="rgba(255,255,255,0.06)" />

      {/* Dual monitors with chart-y content */}
      <rect x={x - 22} y={y - 7} width="20" height="11" fill="#0a0d12" stroke="#3a4150" strokeWidth="0.5" rx="1" />
      <rect x={x - 20} y={y - 6} width="16" height="9" fill="url(#monitorGlow)" rx="0.5" />
      {/* Chart-line on left monitor */}
      <motion.path
        d={`M ${x - 19} ${y - 1} L ${x - 16} ${y - 4} L ${x - 13} ${y - 2} L ${x - 10} ${y - 5} L ${x - 6} ${y - 3}`}
        stroke="url(#chartLine)"
        strokeWidth="0.6"
        fill="none"
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2, repeat: Infinity }}
      />

      <rect x={x + 2} y={y - 7} width="20" height="11" fill="#0a0d12" stroke="#3a4150" strokeWidth="0.5" rx="1" />
      <rect x={x + 4} y={y - 6} width="16" height="9" fill="url(#monitorGlow)" rx="0.5" />
      {/* Bar chart on right monitor */}
      {[0, 1, 2, 3, 4].map((i) => {
        const bh = 2 + ((i * 7) % 6);
        return (
          <rect
            key={i}
            x={x + 5 + i * 3}
            y={y + 2 - bh}
            width="2"
            height={bh}
            fill="#0ecb81"
            opacity="0.7"
          />
        );
      })}

      {/* Coffee mug */}
      <circle cx={x - 22} cy={y + 14} r="2.5" fill="#3a2818" stroke="#1a0e08" strokeWidth="0.5" />
      {/* Sticky note */}
      <rect x={x + 16} y={y + 12} width="6" height="6" fill="#ffe082" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3" />

      {/* Chair (north side of desk; marketer faces south toward monitors) */}
      <rect x={x - 12} y={y + 30} width="24" height="5" fill="#1a1f29" rx="2" />
      <rect x={x - 12} y={y + 33} width="24" height="2" fill="#ec4899" opacity="0.6" rx="2" />
      <rect x={x - 14} y={y + 35} width="28" height="10" fill="#2a2f38" rx="2" />

      {marketer ? (
        <BackOfficeWorker
          x={x}
          y={y + 32}
          type="MARKETING"
          level={marketer.level}
        />
      ) : (
        <text
          x={x}
          y={y + 39}
          textAnchor="middle"
          fontSize="6"
          fill="rgba(236,72,153,0.4)"
          letterSpacing="1"
        >
          VACANT
        </text>
      )}
    </g>
  );
}

function FilingCabinet({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect width="36" height="40" fill="#3a4150" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="1" />
      <rect width="36" height="3" fill="rgba(255,255,255,0.05)" rx="1" />
      {[8, 22, 36].map((cy, i) => (
        <g key={i}>
          <line x1="2" y1={cy - 4} x2="34" y2={cy - 4} stroke="rgba(0,0,0,0.4)" strokeWidth="0.5" />
          <rect x="14" y={cy - 2.5} width="8" height="1.5" fill="rgba(255,255,255,0.15)" rx="0.3" />
          <circle cx="32" cy={cy - 1.7} r="0.7" fill="#5a6370" />
        </g>
      ))}
    </g>
  );
}

function SmallPlant({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <ellipse cx="0" cy="-4" rx="9" ry="7" fill="#264a26" />
      <ellipse cx="-3" cy="-6" rx="6" ry="5" fill="#3a6a3a" />
      <ellipse cx="3" cy="-7" rx="4" ry="5" fill="#4a7a4a" />
      <path d="M -7 4 L -6 14 L 6 14 L 7 4 Z" fill="#3a2418" stroke="rgba(0,0,0,0.4)" strokeWidth="0.4" />
      <ellipse cx="0" cy="4" rx="7" ry="2" fill="#5a3624" />
    </g>
  );
}

// ─── Manager office ──────────────────────────────────────────────────

function ManagerOffice({
  managers,
  marketers,
  onHireManager,
  onHireMarketing,
  onUpgradeSeat,
  busy,
}: {
  managers: StaffMember[];
  marketers: StaffMember[];
  onHireManager: () => void;
  onHireMarketing: () => void;
  onUpgradeSeat: (s: StaffMember) => () => void;
  busy: boolean;
}) {
  const baseX = MANAGER_ZONE.x;
  const w = MANAGER_ZONE.w;
  const cx = baseX + w / 2;
  const totalManagerLevels = managers.reduce((s, m) => s + m.level, 0);
  const marketer = marketers[0];

  // Manager sits at the executive desk, slightly offset left to make room
  // for the marketing manager's small desk on the right of the room.
  const mgrX = baseX + 110;
  const mktX = baseX + w - 60;

  return (
    <g>
      <text
        x={cx}
        y={BACK_OFFICE_TITLE_H + 14}
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill="#d97706"
        letterSpacing="2"
      >
        MANAGER OFFICE
      </text>

      {/* Awards / certificates on the back wall */}
      <AwardsWall x={baseX + 18} y={BACK_OFFICE_TITLE_H + 22} w={w - 36} />

      {/* Executive desk for the manager — kept on the LEFT side so the
       * marketing manager has room on the right. */}
      <ExecutiveDesk
        x={mgrX}
        y={BACK_OFFICE_TITLE_H + 90}
        managers={managers}
      />

      {/* Manager seat tap-target. */}
      <SeatHotzone
        cx={mgrX}
        cy={BACK_OFFICE_TITLE_H + 90 + 36}
        w={60}
        h={60}
        filled={Boolean(managers[0])}
        busy={busy}
        accent="#d97706"
        label={managers[0] ? "↑" : "+"}
        onClick={managers[0] ? onUpgradeSeat(managers[0]) : onHireManager}
      />

      {/* Marketing manager — co-located in the same office. Smaller desk
       * on the right side of the room. */}
      <MarketingDesk
        x={mktX}
        y={BACK_OFFICE_TITLE_H + 100}
        marketer={marketer}
      />
      <SeatHotzone
        cx={mktX}
        cy={BACK_OFFICE_TITLE_H + 130}
        w={56}
        h={80}
        filled={Boolean(marketer)}
        busy={busy}
        accent="#ec4899"
        label={marketer ? "↑" : "+"}
        onClick={marketer ? onUpgradeSeat(marketer) : onHireMarketing}
      />
      {marketer && (
        <BrandFloater
          originX={mktX}
          originY={BACK_OFFICE_TITLE_H + 100}
          level={marketer.level}
        />
      )}

      {/* Live "+%" boost badge that pulses with manager output */}
      {managers.length > 0 && (
        <ManagerBoostBadge
          x={mgrX}
          y={BACK_OFFICE_TITLE_H + 60}
          managers={managers}
        />
      )}

      {/* Tall plant in marble pot on the left (visitor sofa removed — the
       * marketing desk took its corner). */}
      <ExecutivePlant x={baseX + 26} y={BACK_OFFICE_HEIGHT - 50} />

      {/* "ON DUTY" indicator — fires when EITHER manager or marketer is in. */}
      {(totalManagerLevels > 0 || marketer) && (
        <motion.text
          x={baseX + w - 14}
          y={BACK_OFFICE_TITLE_H + 14}
          textAnchor="end"
          fontSize="8"
          fill="#0ecb81"
          fontWeight="700"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          ● ON DUTY
        </motion.text>
      )}
    </g>
  );
}

function AwardsWall({ x, y, w }: { x: number; y: number; w: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* 3 framed certificates */}
      {[0, 1, 2].map((i) => {
        const fw = (w - 16) / 3;
        const fx = i * (fw + 8);
        return (
          <g key={i} transform={`translate(${fx}, 0)`}>
            <rect width={fw} height="22" fill="#5a3a14" stroke="#2a1d08" strokeWidth="0.6" rx="1" />
            <rect x="2" y="2" width={fw - 4} height="18" fill="#f4ecd8" rx="0.5" />
            {/* Gold seal */}
            <circle cx={fw - 7} cy="14" r="3" fill="#f0b90b" stroke="#7a5400" strokeWidth="0.4" />
            <circle cx={fw - 7} cy="14" r="1.4" fill="#bf8a08" />
            {/* Certificate text lines */}
            <line x1="5" y1="7" x2={fw - 14} y2="7" stroke="#5a3a14" strokeWidth="0.4" opacity="0.7" />
            <line x1="5" y1="10" x2={fw - 14} y2="10" stroke="#5a3a14" strokeWidth="0.4" opacity="0.4" />
            <line x1="5" y1="13" x2={fw - 14} y2="13" stroke="#5a3a14" strokeWidth="0.4" opacity="0.4" />
            <line x1="5" y1="16" x2={fw - 14} y2="16" stroke="#5a3a14" strokeWidth="0.4" opacity="0.4" />
          </g>
        );
      })}
    </g>
  );
}

function ExecutiveDesk({
  x,
  y,
  managers,
}: {
  x: number;
  y: number;
  managers: StaffMember[];
}) {
  // Wide L-shaped desk centered at (x, y); managers seat on the south side.
  return (
    <g>
      {/* Desk shadow */}
      <rect x={x - 80} y={y + 26} width="160" height="6" fill="rgba(0,0,0,0.5)" rx="3" />
      {/* Main desk surface */}
      <rect x={x - 80} y={y - 10} width="160" height="36" fill="url(#execDeskGrad)" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="2" />
      <rect x={x - 80} y={y - 10} width="160" height="3" fill="url(#execDeskTop)" rx="2" />
      {/* Side return (L shape extension to the right) */}
      <rect x={x + 60} y={y + 26} width="40" height="22" fill="url(#execDeskGrad)" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="2" />
      <rect x={x + 60} y={y + 26} width="40" height="2" fill="url(#execDeskTop)" rx="2" />
      {/* Wood grain */}
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1={x - 78}
          y1={y - 6 + i * 6}
          x2={x + 78}
          y2={y - 6 + i * 6}
          stroke="rgba(255,220,180,0.04)"
          strokeWidth="0.4"
        />
      ))}
      {/* Sleek monitor */}
      <rect x={x - 22} y={y - 18} width="44" height="14" fill="#0a0d12" stroke="#3a4150" strokeWidth="0.6" rx="1.5" />
      <rect x={x - 20} y={y - 17} width="40" height="12" fill="url(#monitorGlow)" rx="0.7" />
      <rect x={x - 4} y={y - 4} width="8" height="2" fill="#1a1f29" />
      {/* Phone (red landline) — rings more dramatically when no manager
          is seated, suggesting "an opportunity is going unanswered". */}
      <PhoneOnDesk
        x={x - 70}
        y={y + 4}
        empty={managers.length === 0}
      />
      {/* Pen holder */}
      <g transform={`translate(${x + 32}, ${y + 4})`}>
        <rect width="10" height="12" fill="#1a1f29" stroke="rgba(0,0,0,0.4)" strokeWidth="0.4" rx="1" />
        <line x1="2" y1="0" x2="2" y2="-3" stroke="#1f6feb" strokeWidth="0.7" />
        <line x1="5" y1="0" x2="5" y2="-4" stroke="#0ecb81" strokeWidth="0.7" />
        <line x1="8" y1="0" x2="8" y2="-2" stroke="#f6465d" strokeWidth="0.7" />
      </g>
      {/* Stack of papers */}
      <g transform={`translate(${x + 26}, ${y + 18})`}>
        <rect width="14" height="2" fill="#eaecef" stroke="rgba(0,0,0,0.3)" strokeWidth="0.3" />
        <rect y="-2" width="14" height="2" fill="#f4f4f4" stroke="rgba(0,0,0,0.3)" strokeWidth="0.3" />
        <rect y="-4" width="14" height="2" fill="#eaecef" stroke="rgba(0,0,0,0.3)" strokeWidth="0.3" />
      </g>
      {/* Single leather executive chair (manager cap is 1). */}
      <g transform={`translate(${x}, ${y + 36})`}>
        {/* Chair back (tall) */}
        <rect x="-14" y="-4" width="28" height="6" fill="url(#leatherChair)" rx="2" />
        <rect x="-14" y="-4" width="28" height="2" fill="rgba(255,200,100,0.15)" rx="2" />
        {/* Armrests */}
        <rect x="-18" y="2" width="4" height="14" fill="#1a0a0a" rx="1" />
        <rect x="14" y="2" width="4" height="14" fill="#1a0a0a" rx="1" />
        {/* Seat */}
        <rect x="-15" y="2" width="30" height="13" fill="url(#leatherChair)" rx="2" />
        {/* Wheel base hint */}
        <ellipse cx="0" cy="17" rx="6" ry="2" fill="#0a0a0a" />

        {managers[0] && (
          <BackOfficeWorker
            x={0}
            y={-1}
            type="MANAGER"
            level={managers[0].level}
          />
        )}
      </g>

      {/* Decorative guest chair to the side — visually balances the
          executive area without being a hireable seat. */}
      <g transform={`translate(${x + 56}, ${y + 38})`}>
        <rect x="-9" y="-3" width="18" height="4" fill="#3a2418" rx="1.5" />
        <rect x="-10" y="0" width="20" height="11" fill="#5a3624" rx="1.5" />
      </g>

      {!managers[0] && (
        <text
          x={x}
          y={y + 60}
          textAnchor="middle"
          fontSize="7"
          fill="rgba(217,119,6,0.4)"
          letterSpacing="2"
        >
          OFFICE VACANT
        </text>
      )}
    </g>
  );
}

function ExecutivePlant({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Marble pot */}
      <path d="M -14 0 L -12 30 L 12 30 L 14 0 Z" fill="#2a2a2e" stroke="rgba(255,255,255,0.08)" strokeWidth="0.4" />
      <ellipse cx="0" cy="0" rx="14" ry="4" fill="#3a3a3e" />
      <ellipse cx="0" cy="0" rx="11" ry="2.5" fill="#1a1a1e" />
      {/* Marble veining */}
      <path d="M -10 6 Q -4 14 8 22" stroke="rgba(255,255,255,0.05)" strokeWidth="0.4" fill="none" />
      <path d="M 6 4 Q 0 14 -8 24" stroke="rgba(255,255,255,0.04)" strokeWidth="0.3" fill="none" />
      {/* Lush foliage */}
      <ellipse cx="-6" cy="-6" rx="11" ry="9" fill="#1a3a1a" />
      <ellipse cx="6" cy="-8" rx="10" ry="11" fill="#264a26" />
      <ellipse cx="0" cy="-14" rx="9" ry="10" fill="#2e5a2e" />
      <ellipse cx="-8" cy="-12" rx="7" ry="9" fill="#3a6a3a" />
      <ellipse cx="6" cy="-16" rx="6" ry="8" fill="#4a7a4a" />
      <ellipse cx="-2" cy="-20" rx="5" ry="7" fill="#5a8a5a" />
      {/* Tall stem with single leaf */}
      <line x1="0" y1="-26" x2="0" y2="-30" stroke="#3a6a3a" strokeWidth="0.6" />
      <ellipse cx="2" cy="-31" rx="2.5" ry="4" fill="#5a8a5a" />
    </g>
  );
}

// ─── Generic back-office worker sprite ───────────────────────────────

function BackOfficeWorker({
  x,
  y,
  type,
  level,
  compact = false,
}: {
  x: number;
  y: number;
  type: string;
  level: number;
  compact?: boolean;
}) {
  const color = STAFF_COLOR[type] ?? STAFF_COLOR.CASHIER;
  const label = STAFF_LABEL[type]?.[0] ?? "?";

  return (
    <g>
      <g transform={`translate(${x}, ${y})`}>
        <RoleCharacter type={type} level={level} compact={compact} />
      </g>
      {/* Tiny role tag below the worker — outside the character so the label
          stays anchored even when the body sways. */}
      <g transform={`translate(${x - 14}, ${y + 13})`}>
        <rect width="28" height="10" rx="5" fill="rgba(0,0,0,0.55)" stroke={color.suit} strokeWidth="0.4" />
        <text x="14" y="7" textAnchor="middle" fontSize="7" fontWeight="700" fill={color.suit}>
          {label}·L{level}
        </text>
      </g>
    </g>
  );
}

// ─── Live activity indicators (back office) ───────────────────────────

/**
 * Pulses a shield emblem brighter as more / higher-level Security guards
 * are present. Visualizes the "supply" they contribute to the global
 * security pool.
 */
function SecurityShieldPulse({
  x,
  y,
  intensity,
}: {
  x: number;
  y: number;
  intensity: number;
}) {
  // Map total levels (0..15ish) to glow radius (0..8) and pulse rate.
  const glow = Math.min(8, intensity * 1.2);
  const pulseDur = Math.max(1.2, 3.5 - intensity * 0.25);
  return (
    <g transform={`translate(${x}, ${y})`}>
      <motion.circle
        cx="12"
        cy="14"
        r="20"
        fill="rgba(126,170,255,0.15)"
        animate={{
          opacity: intensity > 0 ? [0.15, 0.6, 0.15] : 0.05,
          scale: intensity > 0 ? [0.7, 1.05, 0.7] : 0.7,
        }}
        transition={{ duration: pulseDur, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "12px 14px" }}
      />
      <path
        d="M 12 0 L 24 4 L 24 14 Q 24 22 12 28 Q 0 22 0 14 L 0 4 Z"
        fill="#1f2937"
        stroke="#7eaaff"
        strokeWidth="0.7"
        style={{ filter: `drop-shadow(0 0 ${glow}px #7eaaff)` }}
      />
      <text x="12" y="18" textAnchor="middle" fontSize="10" fill="#7eaaff" fontWeight="700">
        ★
      </text>
      {intensity > 0 && (
        <text
          x="12"
          y="38"
          textAnchor="middle"
          fontSize="6"
          fill="#7eaaff"
          fontWeight="700"
          letterSpacing="1"
        >
          ARMED
        </text>
      )}
    </g>
  );
}

/**
 * Periodic 📣 brand-awareness icon that floats up from a marketer's desk.
 * Pure cosmetic — the actual brand-awareness ticker is server-side. Higher
 * level = more frequent floats.
 */
function BrandFloater({
  originX,
  originY,
  level,
}: {
  originX: number;
  originY: number;
  level: number;
}) {
  // Spawn cadence scales with level: L1 every ~6s, L5 every ~2s.
  const cadenceMs = Math.max(1500, 7000 - level * 1000);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), cadenceMs);
    return () => window.clearInterval(id);
  }, [cadenceMs]);

  return (
    <motion.g
      key={tick}
      initial={{ x: originX, y: originY + 8, opacity: 0, scale: 0.4 }}
      animate={{ y: originY - 28, opacity: [0, 1, 1, 0], scale: 1 }}
      transition={{ duration: 1.6, ease: "easeOut" }}
      style={{ pointerEvents: "none" }}
    >
      <text fontSize="13" textAnchor="middle">
        📣
      </text>
    </motion.g>
  );
}

/**
 * Animated "+N%" badge floating above the executive desk that shows the
 * cumulative manager bonus the player is enjoying in this branch. The
 * "%" pulses on a slow cadence so it reads as a live readout, not a
 * static label.
 */
function ManagerBoostBadge({
  x,
  y,
  managers,
}: {
  x: number;
  y: number;
  managers: StaffMember[];
}) {
  // Mirror the server's managerBonusPct table so the UI value matches
  // (kept in sync manually with apps/api/.../game.config.ts).
  const tablePct = [0, 5, 12, 20, 30, 45];
  const totalPct = managers.reduce((s, m) => {
    const pct = m.level < tablePct.length
      ? tablePct[m.level]
      : 45 + (m.level - 5) * 5;
    return s + pct;
  }, 0);

  return (
    <motion.g
      animate={{ y: [y, y - 2, y] }}
      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
    >
      <rect
        x={x - 24}
        y={y - 9}
        width="48"
        height="18"
        rx="9"
        fill="rgba(217,119,6,0.18)"
        stroke="#d97706"
        strokeWidth="0.7"
      />
      <text
        x={x}
        y={y + 4}
        textAnchor="middle"
        fontSize="10"
        fontWeight="700"
        fill="#d97706"
      >
        +{totalPct}%
      </text>
    </motion.g>
  );
}

/**
 * Phone on the manager's desk. When the manager seat is empty, the phone
 * "rings" — louder pulse, brighter LED, occasional "RING!" word balloon —
 * to nudge the player to hire a manager.
 */
function PhoneOnDesk({ x, y, empty }: { x: number; y: number; empty: boolean }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <motion.g
        animate={empty ? { rotate: [-2, 2, -2, 2, 0] } : { rotate: 0 }}
        transition={
          empty
            ? { duration: 0.5, repeat: Infinity, repeatDelay: 3.5, ease: "easeInOut" }
            : { duration: 0 }
        }
        style={{ transformOrigin: "11px 6px" }}
      >
        <rect width="22" height="12" fill="#7a0a0a" stroke="rgba(0,0,0,0.5)" strokeWidth="0.5" rx="1.5" />
        <rect x="2" y="2" width="18" height="3" fill="#a52828" rx="0.5" />
        {[0, 1, 2].map((i) => (
          <circle key={i} cx={4 + i * 5} cy="9" r="0.8" fill="#3a0a0a" />
        ))}
      </motion.g>
      {/* Status LED — green idle blink, red urgent blink when empty. */}
      <motion.circle
        cx="20"
        cy="2"
        r="1.2"
        fill={empty ? "#f6465d" : "#0ecb81"}
        animate={{ opacity: empty ? [0.2, 1, 0.2] : [0, 1, 0] }}
        transition={{
          duration: empty ? 0.6 : 4,
          repeat: Infinity,
          repeatDelay: empty ? 0 : 6,
        }}
      />
      {/* "RING!" word balloon — only when nobody's there to pick up. */}
      {empty && (
        <motion.g
          animate={{ opacity: [0, 0, 1, 1, 0], y: [0, 0, -4, -4, -4] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeOut" }}
        >
          <rect
            x="-12"
            y="-14"
            width="34"
            height="11"
            rx="5"
            fill="rgba(246,70,93,0.95)"
            stroke="#7a0a0a"
            strokeWidth="0.4"
          />
          <text
            x="5"
            y="-6"
            textAnchor="middle"
            fontSize="7"
            fontWeight="700"
            fill="#fff"
            letterSpacing="1"
          >
            RING!
          </text>
        </motion.g>
      )}
    </g>
  );
}

/**
 * Tiny clipboard-carrying courier that walks across the back office at
 * floor level, looping between zones. Pure flavor — sells "alive office".
 * Always present so the room is never empty-feeling, even before any
 * support staff is hired.
 */
function BackOfficeCourier() {
  const y = BACK_OFFICE_HEIGHT - 14;
  // Path waypoints: enter from manager office, swing to marketing center,
  // dip into security, swing back. ~14s round trip.
  const xs = [VIEW_W - 50, VIEW_W * 0.55, 130, 50, 130, VIEW_W * 0.55, VIEW_W - 50];
  return (
    <motion.g
      animate={{ x: xs }}
      transition={{
        duration: 18,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
        times: [0, 0.18, 0.36, 0.5, 0.64, 0.82, 1],
      }}
      style={{ pointerEvents: "none" }}
    >
      {/* Shadow */}
      <ellipse cx="0" cy={y + 7} rx="6" ry="1.5" fill="rgba(0,0,0,0.5)" />
      {/* Coat */}
      <ellipse cx="0" cy={y - 1} rx="6" ry="5" fill="#374151" />
      <ellipse cx="0" cy={y - 1} rx="6" ry="5" fill="rgba(255,255,255,0.05)" />
      {/* Tie hint */}
      <rect x="-0.6" y={y - 4} width="1.2" height="3" fill="#a52828" />
      {/* Head */}
      <circle cx="0" cy={y - 6} r="3.2" fill="#d9a880" stroke="rgba(0,0,0,0.3)" strokeWidth="0.3" />
      {/* Hair */}
      <path d={`M ${-2.6} ${y - 8} Q 0 ${y - 10.5} ${2.6} ${y - 8}`} fill="#1a1208" opacity="0.85" />
      {/* Clipboard */}
      <rect x="4.5" y={y - 1} width="3.5" height="5" fill="#3a4150" stroke="#1a1f29" strokeWidth="0.3" rx="0.4" />
      <rect x="5" y={y - 0.4} width="2.5" height="0.6" fill="#eaecef" />
      <rect x="5" y={y + 0.6} width="2" height="0.4" fill="#eaecef" opacity="0.5" />
      <rect x="5" y={y + 1.4} width="2.5" height="0.4" fill="#eaecef" opacity="0.5" />
      {/* Subtle bob to suggest walking */}
      <motion.rect
        x="-0.5"
        y={y + 4}
        width="1"
        height="2"
        fill="transparent"
        animate={{ scaleY: [1, 0.7, 1] }}
        transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.g>
  );
}
